import React from 'react';
import { Button, Card, Modal, Nav } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { useAuth } from 'react-oidc-context';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import classNames from 'classnames';
import { nextStep, prevStep, reset } from 'reducers/createListingReducer';
import { ADD_LISTING, GET_LISTINGS } from 'graphql/listing';
import FalconCloseButton from 'components/common/FalconCloseButton';
import SelectLandParcelsForm from './select-parcels';
import ListingDetailsForm from './listing-details';
import SellingOptionsForm from './selling-options';
import PricingForm from './pricing';
import FinalReviewForm from './final-review';

const NavItem = ({ index, step, stepCount, handleNavs, icon, label }) => {
  return (
    <Nav.Item>
      <Nav.Link
        className={classNames('fw-semibold', {
          done: index < stepCount ? step > index : step === stepCount,
          active: step === index
        })}
        onClick={() => handleNavs(index)}
      >
        <span className="nav-item-circle-parent">
          <span className="nav-item-circle">
            <FontAwesomeIcon icon={icon} />
          </span>
        </span>
        <span className="d-none d-md-block mt-1 fs-10">{label}</span>
      </Nav.Link>
    </Nav.Item>
  );
};

NavItem.propTypes = {
  index: PropTypes.number.isRequired,
  step: PropTypes.number.isRequired,
  stepCount: PropTypes.number.isRequired,
  handleNavs: PropTypes.func.isRequired,
  icon: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired
};

const CreateListingModal = ({ show, onClose }) => {
  const dispatch = useDispatch();
  const auth = useAuth();
  const navigate = useNavigate();

  const { selectedLandParcels, uploadedImages } = useSelector(
    state => state.createListing
  );

  const [addListingMutation] = useMutation(ADD_LISTING, {
    refetchQueries: () => [
      {
        query: GET_LISTINGS,
        variables: {
          createdBy: auth.user.profile.sub
        }
      }
    ]
  });

  const {
    control,
    register,
    watch,
    formState: { errors },
    setError,
    clearErrors,
    handleSubmit,
    reset: resetForm
  } = useForm();

  const { steps, currentStep } = useSelector(state => state.createListing);

  const handleClose = () => {
    dispatch(reset());
    resetForm();
    onClose();
  };

  const submit = data => {
    if (currentStep < steps.length) {
      dispatch(nextStep());
    } else {
      handleClose();
      addListingMutation({
        variables: {
          input: {
            title: data.title,
            introduction: data.introduction,
            price: parseFloat(data.price),
            pricingOption: data.pricingOption,
            sellingOption: data.sellingOption,
            landParcels: selectedLandParcels.map(l => ({
              polygon: {
                points: l.coordinates.map(c => ({
                  lng: c[0],
                  lat: c[1]
                }))
              }
            })),
            imageIds: uploadedImages.map(img => img.id)
          }
        },
        onCompleted: data => {
          toast.success('Listing created');
          navigate(`/listings/${data.addListing.id}`);
        },
        onError: () => {
          toast.error('Error creating listing. Please try again.');
        }
      });
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
      fullscreen
    >
      <Modal.Header>
        <Modal.Title>Create a listing</Modal.Title>
        <FalconCloseButton onClick={handleClose} />
      </Modal.Header>
      <Modal.Body>
        <Card className="theme-wizard h-100">
          <Card.Header className={classNames('bg-body-tertiary', 'pb-2')}>
            <Nav className="justify-content-center">
              {steps.map((item, index) => (
                <NavItem
                  key={item.label}
                  index={index + 1}
                  step={currentStep}
                  stepCount={steps.length}
                  handleNavs={() => {}}
                  icon={item.icon}
                  label={item.label}
                />
              ))}
            </Nav>
          </Card.Header>
          <Card.Body className="fw-normal px-md-6 py-4">
            {currentStep === 1 && (
              <SelectLandParcelsForm
                errors={errors}
                setError={setError}
                clearErrors={clearErrors}
              />
            )}
            {currentStep === 2 && (
              <ListingDetailsForm register={register} errors={errors} />
            )}
            {currentStep === 3 && (
              <SellingOptionsForm errors={errors} control={control} />
            )}
            {currentStep === 4 && (
              <PricingForm register={register} errors={errors} watch={watch} />
            )}
            {currentStep === 5 && <FinalReviewForm />}
          </Card.Body>
        </Card>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose}>
          Close
        </Button>
        <Button
          variant="outline-primary"
          disabled={currentStep === 1}
          onClick={() => dispatch(prevStep())}
        >
          Previous
        </Button>
        <Button variant="outline-primary" onClick={handleSubmit(submit)}>
          {currentStep === steps.length ? 'Submit' : 'Next'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

CreateListingModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default CreateListingModal;
